import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    inject,
    OnDestroy,
    OnInit,
    QueryList,
    signal,
    ViewChild,
    ViewChildren
} from '@angular/core';
import {provideNativeDateAdapter} from '@angular/material/core';
import {CommonModule} from '@angular/common';
import {MatSelectModule} from '@angular/material/select';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BehaviorSubject, catchError, combineLatest, filter, lastValueFrom, Observable, of, switchMap, tap} from 'rxjs';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatIcon} from '@angular/material/icon';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatDialog} from '@angular/material/dialog';
import {SideBarDoubleComponent} from '../../../shared/components/side-bar-double/side-bar-double.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute} from '@angular/router';
import {DataTableService} from '../../../core/services/data-table.service';
import {Chart, registerables} from 'chart.js';
import {RegisterHeaderComponent} from '../register/register-header/register-header.component';


import {PageEvent} from '@angular/material/paginator';
import {ChartComponent} from '../../../shared/components/chart/chart.component';
import {TableComponent} from '../../../shared/components/table/table.component';
import {TableServiceService} from './table-service.service';
import {
    ReportGeneralFilterDialogComponent
} from './report-general-filter-dialog/report-general-filter-dialog.component';
import {
    AnalyticService,
    ApiAnalyticReportGeneralFileGetRequestParams,
    ApiAnalyticReportGeneralGetRequestParams,
    GroupService,
    ReferenceService
} from '../../apiModule1';
import {LegendComponent} from './legend/legend.component';
import {ITableColumn} from '../../../core/common/interfaces/table-column.interface';
import {TableCellDirective} from '../../../shared/components/table/table-cell.directive';
import {FilterIndicatorsBarComponent} from '../register/filter-indicators-bar/filter-indicators-bar.component';
import {Sort, SortDirection} from '@angular/material/sort';
import {CommunicationService} from '../../../core/services/communication.service';
import {excludeId} from '../index-entity/index-entity.component';
import {PasFiltersStoreService} from '../../../core/services/pas-store.service';
import {PasFilterType} from '../../../shared/components/side-bar-double/menu-data.service';
import {ToPdfDirective} from "../../../core/directives/to-pdf.directive";
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatProgressBar} from "@angular/material/progress-bar";
import {map} from "rxjs/operators";

Chart.register(...registerables);

export interface LegendInterface {
    high: Legend;
    middle: Legend;
    low: Legend
}

export interface TableData {
    id: number;
    name: string;
    summary: number;

    [key: `${typeof CELL_NAME}${number}`]: {
        id: number;
        name: string;
        value: number;
    };
}

@Component({
    selector: 'report-general',
    standalone: true,
    providers: [
        provideNativeDateAdapter(),
        {provide: DataTableService, useClass: TableServiceService}
    ],
    imports: [
        MatSelectModule,
        MatSidenavModule,
        MatIcon,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        CommonModule,
        MatDatepickerModule,
        SideBarDoubleComponent,
        RegisterHeaderComponent,
        ChartComponent,
        TableComponent,
        LegendComponent,
        TableCellDirective,
        FilterIndicatorsBarComponent,
        ToPdfDirective,
        MatProgressSpinner,
        MatProgressBar,

    ],
    templateUrl: './report-general.component.html',
    styleUrls: ['./report-general.component.scss', '../../../shared/components/table/table.component.scss', '../register/register.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class ReportGeneralComponent implements OnInit, OnDestroy {
    analyticsService = inject(AnalyticService);
    private communicationService = inject(CommunicationService);
    filterStore = inject(PasFiltersStoreService);

    public opened = true;
    mobileOpen = false;
    protected showProgressBar: boolean = false;

    @ViewChild(TableComponent) table!: TableComponent<any>;
    @ViewChild(ToPdfDirective) toPdf?: ToPdfDirective;
    @ViewChildren(ChartComponent) charts!: QueryList<ChartComponent>;

    protected type: PasFilterType = 'report-general'

    list = new BehaviorSubject<void>(undefined);

    updateList = new BehaviorSubject<void>(undefined);

// Pie
//   public pieChartType: ChartType = 'pie';
//   public pieChartLabels = ['Зеленая зона', 'Жёлтая зона', 'Красная зона'];
    summaryPie: number[] = [];
    pies: AnalyticPieListItemNumbers[] = [];

    legend: LegendInterface = {
        low: new Legend(0, 55, 'учреждение недостаточно эффективно'),
        middle: new Legend(55, 80, 'учреждение эффективное'),
        high: new Legend(80, 100, 'учреждение высокоэффективное'),
    }

    private readonly emptyPager: Pager = {
        length: 0,
        pageIndex: 1,
        pageSize: 10,
        previousPageIndex: 0
    };

    initialFilterData: ApiAnalyticReportGeneralGetRequestParams = {
        periodId: 1,
        year: 2024,
        organizationGroupIds: [26]
    }

    search$ = new BehaviorSubject<string>('');
    sort$ = new BehaviorSubject<Sort>({direction: '', active: ''});
    filters$ = new BehaviorSubject<ApiAnalyticReportGeneralGetRequestParams | null>(null);
    filtersFromDialog$ = new BehaviorSubject<ApiAnalyticReportGeneralGetRequestParams | null>(null);
    pager$ = new BehaviorSubject<PageEvent>(this.emptyPager);

    searchObservable$ = this.search$.asObservable();
    sortObservable$ = this.sort$.asObservable();
    filtersObservable$ = this.filters$.asObservable();
    pagerObservable$ = this.pager$.asObservable();

    public searchPhrase: string = '';
    public rawData: TableData[] | null = null;

    referenceService = inject(ReferenceService);
    periodName: string = '';

    groupService = inject(GroupService);
    groupName: string = '';
    pdfReportStatus = signal<boolean>(false);
    importingExcel = signal<boolean>(false);

    constructor(
        private route: ActivatedRoute,
        private destroyRef: DestroyRef,
        private dataTableService: DataTableService,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
    )
    {
        console.log('this.route.data', this.route.data);
        this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({group}) => {
            console.log('group', group);
            this.dataTableService.setData(group);
        });
    }

    get tableData(): any[] | undefined {
        const res = this.dataTableService.getData() as unknown as { pager: unknown, values: any[] };
        return res?.values;
    }

    refreshData(): Observable<any> {
        return combineLatest([this.searchObservable$, this.sortObservable$, this.filtersObservable$, this.pagerObservable$])
            .pipe(
                tap(() => {
                    this.showProgressBar = true;
                    this.cdr.markForCheck();
                }),
                switchMap(() => {
                        return this.analyticsService.apiAnalyticReportGeneralGet(
                            {
                                // sortColumnName: this.sort$.value.active ? this.sort$.value.active : undefined,
                                // sortAscending: this.sort$.value.direction === 'asc' ? true : undefined,
                                ...this.filters$.value,
                            }
                        );
                    }
                ))
            .pipe(catchError(err => {
                console.error(err);
                this.communicationService.publish('ошибка запроса данных', {type: 'error'});
                return of(null);
            }));
    }

    ngOnInit(): void {
        this.dataTableService.showCustomColumns = false;

        const filterObserver = async (v: any) => {
            console.log('filter', v);

            //--- ищем название текущего периода ---
            const periods = await lastValueFrom(this.referenceService.apiReferencePeriodsGet()
                .pipe(map(v => v.values)))
            console.log('periods', periods);
            const periodName: CriterionPeriod | undefined = periods
                && periods.length
                && periods.find((p: CriterionPeriod) => p.id === v?.periodId);
            this.periodName = periodName?.name || '';

            //--- ищем название группы учреждения ---
            const groups = await lastValueFrom(this.groupService.apiGroupGet({}).pipe(map(v => v.values)));
            const group: Group = groups.find((g: Group) => g.id === v?.organizationGroupIds?.[0]);
            this.groupName = group?.shortName || 'не задано';
        }

        //получаем, сохраненные в storage данные
        const readFilterFromStore = this.filterStore.get('report-general')
        this.filters$.next(readFilterFromStore)
        this.filtersFromDialog$.next(readFilterFromStore)

        this.refreshData().subscribe((v: Analytic) => {
            this.setData(v);
            const tableData: TableData[] = this.dataTableService.getData().values;
            this._setTableData(
                replaceIdToOrder(columnSort('summary', 'desc', tableData))
            )
            this.rawData = this.dataTableService.getData().values
            this.table?.matSort && (this.table.matSort.active = 'summary');
            this.table?.matSort && (this.table.matSort.direction = 'desc');

            this.filtersFromDialog$.next(v.filter)

            this.showProgressBar = false;
            this.cdr.markForCheck();
        })


        //сохраняем данные в storage при каждом изменении фильтра в форме
        this.filtersFromDialog$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
            if (!v) return;
            this.filterStore.set(this.type, v);
        });

        //если "report-general" не были сохранены, то мы должны установить значение сабжектов, так, чтобы не вызвать
        // эмит (как следствие повторый запрос)
        this.filters$
            .pipe(
                filter(()=>!!readFilterFromStore),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(filterObserver)
    }

    ngOnDestroy() {
        window.URL.revokeObjectURL(this.url);
    }


    setData(v: Analytic) {
        //--- устанавливаем легенду
        const mapNorms = v.legend[0].norms.map(({min, max, text}: {
            min: number,
            max: number,
            text: string
        }) => new Legend(min, max, text));
        this.legend = {
            high: mapNorms[2],
            middle: mapNorms[1],
            low: mapNorms[0],
        }
        //--- устанавливаем табличные данные
        this.dataTableService.setData({pager: {}, values: this.setTableDate([...v.table])});
        //--- устанавливаем pie данные

        this.setPiesDate(v.pieTable);

        this.cdr.markForCheck();

    }


    setTableDate(table: AnalyticTableRow[]): TableData[] {
        const tableData = analyticTableToData(table);
        console.log('tableData', tableData);
        const columnsData: ITableColumn[] = analyticTableToColumns(tableData.values);
        console.log('columnsData', columnsData);
        this.dataTableService.setColumns(columnsData);
        this.table?.setColumns();

        const res = replaceIdToOrder(tableData.values as { id: number }[]) as TableData[];
        return res;
    }

    setPiesDate(pies: AnalyticPieTable): void {
        if (!pies) return;
        setTimeout(() => {
            this.summaryPie = pies.summary?.parts.map((v: AnalyticPart) => v.count);
            this.pies = [...pies.pieList.map((v: AnalyticPieListItem) => ({...v, parts: this.pieMapCount(v.parts)}))];
            this.cdr.markForCheck();
        }, 200)
    }


    onPageChange(pager: PageEvent): void {
        this.pager$.next(pager);
    }

    onSortedColumn($event: any): void {
        console.log('sortedColumn', $event);
        console.log('table', this.dataTableService.getData())
        const tableData: TableData[] = this.dataTableService.getData().values;

        const reverseDirect = $event.direction === 'desc' ? 'asc' : ($event.direction === '' ? '' : 'desc');

        if ($event.direction === '' && this.rawData) {
            return this._setTableData(this.rawData)
        }

        if ($event.active.startsWith(CELL_NAME)) {
            this._setTableData(
                replaceIdToOrder(cellSort($event.active, reverseDirect, tableData), $event.direction)
            )
        } else {
            this._setTableData(
                replaceIdToOrder(columnSort($event.active, reverseDirect, tableData), $event.direction)
            )
        }

    }

    private _setTableData(data: TableData[]): void {
        this.dataTableService.setData({pager: {}, values: data});
        this.cdr.markForCheck();
    }

    openFilterDialog() {
        this.dialog.open(ReportGeneralFilterDialogComponent, {
            width: '690px',
            height: '100vh',
            position: {
                right: '0px'
            },
            data: this.filtersFromDialog$.value
        }).afterClosed().subscribe((v: ApiAnalyticReportGeneralGetRequestParams) => {
            if (!v) return;
            this.filtersFromDialog$.next(v);
            this.filters$.next(this._prepareFilterToSend(v));
        })
    }

    protected _prepareFilterToSend<T extends object>(filterObj: T): ApiAnalyticReportGeneralGetRequestParams {
        let res = Object.entries(filterObj)
        res = res.filter(([_, value]) => value !== null && value !== undefined)
        res = res.map(([k, v]) => {
            return [k, excludeId(v)]
        })
        const result = Object.fromEntries(res);
        return result
    }

    pieMapCount(parts: AnalyticPart[]) {
        if (!parts || !parts.length) return [];
        return parts.map((part: AnalyticPart) => part.count);
    }

    url: string = '';

    excel() {
        this.importingExcel.set(true);
        const params: ApiAnalyticReportGeneralFileGetRequestParams = {
            periodId: this.filters$.value?.periodId,
            year: this.filters$.value?.year,
            quarter: this.filters$.value?.quarter,
            organizationGroupIds: this.filters$.value?.organizationGroupIds
        }
        this.analyticsService.apiAnalyticReportGeneralFileGet(params, 'body', false, {httpHeaderAccept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'})
            .subscribe((v: Blob) => {
                this.importingExcel.set(false);
                this.url = window.URL.createObjectURL(v);
                const a = document.createElement('a');
                a.href = this.url;
                a.download = 'report.xlsx';
                a.click();
            })
    }

    onSearch(): void {
        this.search$.next(this.searchPhrase);
    }

    public resetFilters(): void {
        // this.filters$.next(this.initialFilterData);
        // this.filtersFromDialog$.next(this.initialFilterData);
        this.filters$.next(null);
        this.filtersFromDialog$.next(null);
    }

    refresh(): void {
        window.location.reload();
    }

    async onExportPdf() {
        if (!this.toPdf) return;
        await this.toPdf.snapshot(() => {
            this.charts.forEach(c => c.refresh())
        }, 'general-report')
    }

    protected readonly Math = Math;
    protected loading: boolean = false;

}

export const CELL_NAME = 'cell'

export class Legend {
    public readonly min: number;
    public readonly max: number;
    public readonly description: string;
    public readonly title: string;

    constructor(min: number, max: number, description: string) {
        this.min = min;
        this.max = max;
        this.description = description;
        this.title = this.createTitle()
    }

    createTitle(): string {
        if (this.min === 0) {
            return `до ${this.max}`
        }
        if (this.max === 100) {
            return `от ${this.min}`
        }
        return `от ${this.min} до ${this.max}`
    }

}

function analyticTableToData(table: AnalyticTableRow[]): { pager: {}, values: unknown[] } {
    const values: unknown[] = table.map((v: AnalyticTableRow) => {
        const cells: [string, AnalyticCell][] = v.cells.map((cell: AnalyticCell, i: number) => {
            return [CELL_NAME + i, cell]
        })
        const objectCells = Object.fromEntries(cells)
        return {
            id: v.id,
            name: v.name,
            summary: v.summary,
            ...objectCells,
        }
    });
    return {pager: {}, values}
}

function analyticTableToColumns(value: unknown[]): ITableColumn[] {
    if (!value || !value.length) return [];
    const entries = Object.entries(value[0] as any);
    const res = entries.map(([key, value]): ITableColumn => {
        const name = (value as any)?.name;
        let title = key.startsWith(CELL_NAME) ? name : key;
        if (title === 'name') {
            title = 'Учреждение'
        }
        if (title === 'summary') {
            title = 'Общий рейтинг'
        }
        if (title === 'id') {
            title = '№'
        }
        return {
            name: key,
            title,
            default: true,
            useCustomTemplate: !(key === 'id' || key === 'name'),
            maxWidth: key === 'name' ? '25vw' : undefined,

        }
    })
    return res;
}

export function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function replaceIdToOrder<T extends { id: number }>(v: T[], direction: 'asc' | 'desc' = 'asc'): T[] {

    const fn = (direction: 'asc' | 'desc', val: number): number => {
        if (direction === 'asc') {
            return val;
        } else {
            return v.length - val + 1
        }
    }

    return v.map((e: T, idx: number) => ({...e, id: fn(direction, idx + 1)}));
}

export function cellSort(active: string, direction: SortDirection, tableData: TableData[]): TableData[] {
    if (direction === '') return tableData

    const fn = direction === 'asc'
        ? (a: TableData, b: TableData) => (a as Record<string, any>)[active].value - (b as Record<string, any>)[active].value
        : (b: TableData, a: TableData) => (a as Record<string, any>)[active].value - (b as Record<string, any>)[active].value;
    const data = tableData.toSorted(fn)
    return data
}

export function columnSort(active: string, direction: SortDirection, tableData: TableData[]): TableData[] {
    if (direction === '') return tableData

    const fn: (a: TableData, b: TableData) => 1 | -1 | 0 = direction === 'asc'
        ? (a: TableData, b: TableData) => (a as Record<string, any>)[active] > (b as Record<string, any>)[active] ? 1 : -1
        : (a: TableData, b: TableData) => (a as Record<string, any>)[active] <= (b as Record<string, any>)[active] ? 1 : -1;
    const data = tableData.toSorted(fn)
    return data
}
